// @ts-nocheck
/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { useFormik } from 'formik';
import { clone, cloneDeep, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { object, string, boolean, number } from 'yup';

import { ReactComponent as DeleteIcon } from '../../assets/images/Delete.svg';
import { ReactComponent as DropdownArrow } from '../../assets/images/DropdownArrow.svg';
import API_ENDPOINTS from '../../const/ApiEndpoints';
import { DIALOG_ICON_BUTTON_CSS } from '../../const/CommonConst';
import { PodSpecs } from '../../const/ConsoleConst';
import REGEX from '../../const/Regex';
import utils from '../../utils/utils';
import { SwalToast } from '../Common';
import ConsoleArgumentInput from '../Common/ConsoleArgumentInput';
import { DropDown, DropdownItem } from '../Common/DropDown';
import ModelMetaDataSchemaDropdown from '../Common/ModelMetaDataSchemaTreeDropdown';
import DialogTransition from '../Common/Transitions/DialogTransition';
import DocTreeDropdown from '../Documentation/DocTreeDropdown';
import NormalOperationRenderer from './NormalOperationRenderer';
import useNewDocumentation from '../../Hooks/NewDocumentation/useNewDocumentation';

const outputTypesCanbeAddedToDoc = ['table', 'chart', 'image', 'test_case'];
const nodeDisplayNameMinLength = 2;
const nodeDisplayNameMaxLength = 200;
const regex = /^pipeline_args\((\w+),\s*Column(,\s*(\w+))?\)$/;
const argsTypesForValidationCheck = ['String', 'Float', 'Int', 'Boolean'];

const ARGUMENT_TYPE = {
  CONSOLE_FILES: 'ConsoleFiles',
};

// todo: in console move this function to utils and use here and in console. so that we will have common defination

/**
 * @function formatArg
 * @description It is used to format the argument.
 * @param {object} arg - argument object to be formatted.
 * @param {array} models - list of all models.
 * @param {array} datasets - list of all datasets.
 * @param {function} fetchDatasetColumnDetails - function to fetch the dataset column details.
 * @param {object} activeProject - active project object.
 * @returns formatted argument object.
 */
const formatArg = async (
  arg,
  models,
  datasets,
  consoleFiles,
  fetchDatasetColumnDetails,
  activeProject,
  pipelineArgumentsState
) => {
  switch (arg.type) {
    case 'ConsoleFiles':
      if (!consoleFiles.length) {
        return Promise.reject();
      }
      return {
        ...arg,
        value: consoleFiles?.find(
          (file) =>
            file?.id === parseInt(arg?.value, 10) ||
            file.id === arg.value ||
            file.file_id === arg.value
        ),
      };
    case 'Model':
      if (!models.length) return Promise.reject();
      return {
        ...arg,
        value: models.find((model) =>
          model?.modelId
            ? model?.modelId === arg?.value
            : model?.id === arg?.value
        ),
        type: arg?.type,
      };
    case 'Dataset':
      return {
        ...arg,
        value: datasets.find(
          (dataset) =>
            dataset.dataset_id === arg.value || dataset.id === arg.value
        ),
        type: arg.type,
      };
    case 'Column': {
      const dataset = datasets.find((ds) =>
        ds?.dataset_id
          ? ds?.dataset_id === arg?.dataset
          : ds?.id === arg?.dataset
      );

      if (!dataset) {
        return {
          ...arg,
          dataset: {},
          value: [],
          type: arg.type,
        };
      }

      const response = await fetchDatasetColumnDetails({
        project_id: activeProject?.project_id,
        dataset_id: dataset?.id,
      });

      let datasetColumns = response?.data?.columns || [];

      let value;
      if (regex.test(arg?.dataset)) {
        value = datasetColumns.filter((col) =>
          arg.parent_arg_value?.includes(col.column_name)
        );
      } else {
        value = datasetColumns.filter((col) =>
          arg?.value?.includes(col?.column_name)
        );
      }

      // If value is still empty, fallback to `arg.datasetColumns`
      if (value.length === 0) {
        datasetColumns = arg?.datasetColumns || [];

        if (regex.test(arg?.dataset)) {
          value = datasetColumns.filter((col) =>
            arg.parent_arg_value?.includes(col.column_name)
          );
        } else {
          value = datasetColumns.filter((col) =>
            arg?.value?.includes(col?.column_name)
          );
        }
      }

      return {
        ...arg,
        dataset,
        value,
        type: arg.type,
      };
    }
    case 'Boolean':
      return {
        ...arg,
        value: arg.parent_arg_value ? arg.parent_arg_value : arg.value,
        type: arg.type,
      };
    default:
      // eslint-disable-next-line no-case-declarations
      const parentOption = pipelineArgumentsState[arg.type].find(
        (ele) => ele.value === arg.value
      );
      return {
        ...arg,
        type: arg?.type,
        value: parentOption || arg.value,
      };
  }
};

const datasetGetOptionLabel = (option) => {
  if (
    option?.table_display_name &&
    !option?.argumentsFromPipeline &&
    !option?.argumentsFromParentPipeline
  ) {
    return utils.getDisplayName(option, 'table');
  }
  if (option?.argumentsFromPipeline || option?.argumentsFromParentPipeline) {
    return utils.getDisplayName(option, 'pipeline');
  }
  return '';
};

const extractArgNameFromExpression = (str) => {
  const regexFirstArg = /\(([^,]+)/;
  const match = str.match(regexFirstArg);
  if (match) {
    return match[1].trim();
  }
  return null;
};

/**
 * @function getIncomingNodeIds
 * @description It is used to get the incoming node ids.
 * @param {object} node - Object of the node.
 * @param {array} edges - list of all edges.
 * @returns {array} list of incoming node ids.
 */
const getIncomingNodeIds = (node, edges = []) => {
  const incomingNodes = [];
  edges?.forEach((element) => {
    if (String(node.node_id) === element.target) {
      incomingNodes.push(element?.source);
    }
  });
  return incomingNodes;
};

/**
 * @function opACtiveDatasetOnChange
 * @description  Returns the label for the active dataset option in the operation form.
 * @param {object} option - The dataset option object.
 * @returns {string} - The label for the option.
 */
const opActiveDatasetGetOptionLabel = (option) => {
  if (
    option?.table_display_name &&
    !option?.argumentsFromPipeline &&
    !option?.argumentsFromParentPipeline
  ) {
    return utils.getDisplayName(option, 'table');
  }
  if (option?.argumentsFromPipeline || option?.argumentsFromParentPipeline) {
    return utils.getDisplayName(option, 'pipeline');
  }
  return '';
};

/**
 * @function PipelineNodeEditorModal
 * @description It is used to render the pipeline node editor modal.
 * @param {object} props - props passed to the component.
 * @param {boolean} props.open - flag to check if modal is open.
 * @param {function} props.handleClose - function to close the modal.
 * @param {object} props.nodeData - node data object.
 * @param {function} props.setNodeData - function to set the node data.
 * @param {object} props.activeProject - details of the active project.
 * @param {array} props.datasetList - list of all datasets.
 * @param {function} props.fetchDatasetColumnDetails - function to fetch the dataset column details.
 * @param {function} props.fetchAllModels - function to fetch all models.
 * @param {function} props.updateNode - function to update the node.
 * @param {object} props.pipelineData - pipeline data object.
 * @param {function} props.deleteNode - function to delete the node.
 * @param {string} permission - contains the permission of the user
 * @param {object} docEditorOverview - object that contains the documentation overview
 * @param {function} fetchEditorOverview - function to fetch the documentation overview
 * @param {object} allPermissions - contains all the permissions
 * @returns {object} React component.
 */
const PipelineNodeEditorModal = ({
  open,
  handleClose,
  nodeData,
  activeProject,
  datasetList,
  fetchDatasetColumnDetails,
  fetchAllModels = () =>
    new Promise((resolve) => {
      resolve([]);
    }),
  updateNode,
  pipelineData,
  deleteNode,
  docEditorOverview,
  fetchEditorOverview,
  allPermissions,
  modeleMetaDataSchema,
  showToModelMetaDataOption,
  consoleFileList,
  fetchPods,
  updateNodePermission,
  deleteNodePermission,
  readOnly = false,
  fetchConnectedPipelineArguemnts,
}) => {
  const theme = useTheme();
  const [accordianState, setAccordianState] = useState('panel1');
  const [allModels, setAllModels] = useState([]);
  const { nodes, edges } = pipelineData.flow_display;
  const [additionalDatasetsFromPrevNode, setAdditionalDatasetsFromPrevNode] =
    useState([]);
  const [condaEnvironments, setCondaEnvironments] = useState([]);
  const [isCondaEnabled, setIsCondaEnabled] = useState(false);
  const [selectedWorkerMetaData, setSelectedWorkerMetaData] = useState(null);
  const [additionalModelsFromPrevNode, setAdditionalModelsFromPrevNode] =
    useState([]);

  const [
    additionalDatasetsFromPipelineArguments,
    setAdditionalDatasetsFromPipelineArguments,
  ] = useState([]);
  const [
    additionalModelFromPipelineArguments,
    setAdditionalModelFromPipelineArguments,
  ] = useState([]);

  const [
    additionalConsoleFilesFromPipelineArguments,
    setAdditionalConsoleFilesFromPipelineArguments,
  ] = useState([]);

  const [confirmNodeDelete, setConfirmNodeDelete] = useState(false);
  const [placeholderList, setPlaceholderList] = useState([]);

  const [operationForm, setOperationForm] = useState(null);
  const [operationFormattedPayload, setOperationFormattedPayload] =
    useState(null);
  const [nodeParameters, setNodeParameters] = useState(
    nodeData?.instances?.payload || {}
  );
  const typographyColor = 'text.secondary';
  const UNAUTHORIZED_MESSAGE = 'You are not authorized to access this!';
  const PROJECT_COMPLETED = 'Project has been marked completed';
  const [allWorkersList, setAllWorkersList] = useState([]);
  const [isExecutedInRemoteWorker, setIsExecutedInRemoteWorker] =
    useState(false);
  const { fetchPlaceholderList } = useNewDocumentation();
  const [additionalPipelineArguments, setAdditionalPipelineArguments] =
    useState({
      String: [],
      Float: [],
      Int: [],
      Boolean: [],
    });

  const onFormSubmit = useCallback(
    (formValues) => {
      if (operationForm) {
        operationForm?.validateForm().then((res) => {
          operationForm.setTouched(res);
        });
      }
      let metaData = {
        use_conda: false,
      };

      if (formValues?.worker_name?.worker_name) {
        const cpuFieldPayload = {
          cpu_limit: Number(formValues?.cpu_limit) || null,
          memory_limit: Number(formValues?.memory_limit) || null,
        };
        if (formValues?.condaEnvironment && selectedWorkerMetaData) {
          const selectedEnv = selectedWorkerMetaData?.envs?.find(
            (env) => env?.name === formValues?.condaEnvironment
          );

          if (selectedEnv) {
            metaData = {
              use_conda: true,
              env: {
                name: selectedEnv.name,
                path: selectedEnv.path,
              },
              ...cpuFieldPayload,
            };
          } else {
            metaData = {
              use_conda: true,
              env: {
                name: formValues?.condaEnvironmentMetaData?.name,
                path: formValues?.condaEnvironmentMetaData?.path,
              },
              ...cpuFieldPayload,
            };
          }
        } else {
          metaData = {
            use_conda: false,
            ...cpuFieldPayload,
          };
        }
      }
      if (!operationForm?.isValid && nodeData?.node_job_type === 'normal') {
        return;
      }

      const updatedArgs = formValues?.args?.map((arg) => {
        if (arg.type === ARGUMENT_TYPE.CONSOLE_FILES && arg.value?.value) {
          return {
            ...arg,
            value: {
              ...arg.value,
              id: arg.value?.value,
            },
          };
        }
        return arg;
      });

      const copyOfNode = {
        data: {
          ...nodeData?.data,
          udfDetails: {
            ...nodeData?.data?.udfDetails,
            args: updatedArgs,
          },
        },
        project_id: nodeData?.project_id,
        pipeline_id: nodeData?.instances?.pipeline_id,
        node_display_name: nodeData?.node_display_name,
        node_instance: { ...nodeData?.instances },
      };
      copyOfNode.node_display_name = formValues?.nodeDisplayName;
      copyOfNode.node_instance.payload.args = formValues?.args?.length
        ? utils.prepareArgsConsoleForPipeline(formValues?.args)
        : [];
      copyOfNode.node_instance.payload.pod_spec = formValues?.pod_spec;
      if (nodeData?.node_job_type !== 'normal') {
        copyOfNode.node_instance.payload.worker_name =
          formValues?.worker_name !== ''
            ? formValues?.worker_name?.worker_name
            : null;
      }

      if (formValues?.opActiveDataset?.dataset_id) {
        copyOfNode.node_instance.payload.dataset_id =
          formValues.opActiveDataset.dataset_id;
      }
      const documentationDetailObj = cloneDeep(formValues.documentation_detail);
      if (
        documentationDetailObj?.length &&
        window.CONFIG.NEW_DOCUMENTATION === 'false'
      ) {
        let exitFlag = false;
        documentationDetailObj?.forEach((docMapping, index) => {
          if (docMapping.type === 'table') {
            if (
              (isEmpty(docMapping?.payload?.table_id) ||
                docMapping?.payload?.table_id?.parent === -1) &&
              isEmpty(docMapping?.payload?.comment) &&
              isEmpty(docMapping?.payload?.table_heading)
            ) {
              exitFlag = false;
              documentationDetailObj.splice(index, 1);
            } else {
              if (
                isEmpty(docMapping?.payload?.table_heading) ||
                isEmpty(docMapping?.payload?.table_id)
              ) {
                exitFlag = true;
              }
              if (isEmpty(docMapping?.payload?.comment)) {
                // eslint-disable-next-line no-param-reassign
                docMapping.payload.comment = '';
              }
            }
          }
          if (docMapping.type === 'test_case') {
            if (
              (isEmpty(docMapping?.payload?.test_case_id) ||
                docMapping?.payload?.test_case_id?.parent === -1) &&
              isEmpty(docMapping?.payload?.comment) &&
              isEmpty(docMapping?.payload?.table_heading)
            ) {
              exitFlag = false;
              documentationDetailObj.splice(index, 1);
            } else {
              if (
                isEmpty(docMapping?.payload?.table_heading) ||
                isEmpty(docMapping?.payload?.test_case_id)
              ) {
                exitFlag = true;
              }
              if (isEmpty(docMapping?.payload?.comment)) {
                // eslint-disable-next-line no-param-reassign
                docMapping.payload.comment = '';
              }
            }
          }
          if (docMapping.type === 'chart') {
            if (
              (isEmpty(docMapping?.payload?.img_id) ||
                docMapping?.payload?.img_id?.parent === -1) &&
              isEmpty(docMapping?.payload?.comment) &&
              isEmpty(docMapping?.payload?.img_heading)
            ) {
              exitFlag = false;
              documentationDetailObj.splice(index, 1);
            } else {
              if (
                isEmpty(docMapping?.payload?.img_heading) ||
                isEmpty(docMapping?.payload?.img_id)
              ) {
                exitFlag = true;
              }
              if (isEmpty(docMapping?.payload?.comment)) {
                // eslint-disable-next-line no-param-reassign
                docMapping.payload.comment = '';
              }
            }
          }
        });
        if (exitFlag) {
          SwalToast({
            icon: 'error',
            title:
              'Heading and document node is required for declared output and document mapping.',
          });
          return;
        }
      }
      if (
        documentationDetailObj?.length &&
        window.CONFIG.NEW_DOCUMENTATION === 'true'
      ) {
        let exitFlag = false;
        documentationDetailObj?.forEach((docMapping, index) => {
          if (docMapping.type === 'table') {
            if (
              !docMapping?.payload?.placeholder_id &&
              isEmpty(docMapping?.payload?.heading) &&
              isEmpty(docMapping?.payload?.description)
            ) {
              exitFlag = false;
              documentationDetailObj.splice(index, 1);
            } else {
              if (
                isEmpty(docMapping?.payload?.heading) ||
                !docMapping?.payload?.placeholder_id
              ) {
                exitFlag = true;
              }
              if (isEmpty(docMapping?.payload?.description)) {
                // eslint-disable-next-line no-param-reassign
                docMapping.payload.description = '';
              }
            }
          }
          if (docMapping.type === 'test_case') {
            if (
              !docMapping?.payload?.placeholder_id &&
              isEmpty(docMapping?.payload?.heading) &&
              isEmpty(docMapping?.payload?.description)
            ) {
              exitFlag = false;
              documentationDetailObj.splice(index, 1);
            } else {
              if (
                isEmpty(docMapping?.payload?.heading) ||
                !docMapping?.payload?.placeholder_id
              ) {
                exitFlag = true;
              }
              if (isEmpty(docMapping?.payload?.comment)) {
                // eslint-disable-next-line no-param-reassign
                docMapping.payload.description = '';
              }
            }
          }
          if (docMapping.type === 'chart') {
            if (
              !docMapping?.payload?.placeholder_id &&
              isEmpty(docMapping?.payload?.heading) &&
              isEmpty(docMapping?.payload?.description)
            ) {
              exitFlag = false;
              documentationDetailObj.splice(index, 1);
            } else {
              if (
                isEmpty(docMapping?.payload?.heading) ||
                !docMapping?.payload?.placeholder_id
              ) {
                exitFlag = true;
              }
              if (isEmpty(docMapping?.payload?.description)) {
                // eslint-disable-next-line no-param-reassign
                docMapping.payload.description = '';
              }
            }
          }
        });
        if (exitFlag) {
          SwalToast({
            icon: 'error',
            title:
              'Heading and placeholder is required for declared output and document mapping.',
          });
          return;
        }
      }

      // ---------- end ----------------------------------
      // react-flow not allowing updates to level one properties in object hence adding this inside data.
      copyOfNode.data.documentation_detail = documentationDetailObj;

      if (
        formValues.enableAddingModelMetaData &&
        formValues.modelMetaDataDetail?.length === 0
      ) {
        SwalToast({
          icon: 'error',
          title:
            'Model metadata schema node is required for declared output and document mapping.',
        });
        return;
      }
      // react-flow not allowing updates to level one properties in object hence adding this inside data.
      copyOfNode.data.model_meta_data_detail = cloneDeep(
        formValues.modelMetaDataDetail
      );

      // copyOfNode.data.modelMetaDataDetail =
      // we are not updating declared output as those are made readonly.
      const updatePayloadConsole = {
        ...copyOfNode,
        node_instance: {
          ...copyOfNode?.node_instance,
          node_instance_id: copyOfNode?.node_instance?.id,
          payload: {
            ...copyOfNode.node_instance.payload,
            pipeline_id: copyOfNode?.pipeline_id,
            meta_data: metaData,
          },
          model_meta_data_detail: nodeData?.instances?.model_meta_data_detail,
          documentation_detail: copyOfNode.data.documentation_detail,
        },
        node_id: nodeData?.instances?.node_id,
        node_name: nodeData?.node_name,
        node_display_name: copyOfNode?.node_display_name,
        project_id: activeProject?.project_id,
      };

      delete updatePayloadConsole?.node_instance?.node_id;
      delete updatePayloadConsole?.payload;
      delete updatePayloadConsole?.documentation_detail;

      const updatePayloadNormalOperation = {
        ...copyOfNode,
        node_instance: {
          ...copyOfNode.node_instance,
          node_instance_id: copyOfNode?.node_instance?.id,
          payload: {
            // eslint-disable-next-line no-use-before-define
            ...operationFormattedPayload,
            ...(formValues?.opActiveDataset?.dataset_id
              ? { dataset_id: formValues.opActiveDataset.dataset_id }
              : {}),
            // eslint-disable-next-line no-use-before-define
            table_name: formikForm?.values?.opActiveDataset?.Name,
            ...(copyOfNode?.node_instance?.payload?.action
              ? { action: copyOfNode.node_instance.payload?.action }
              : {}),
            target_column: operationFormattedPayload?.target_column,
            worker_name: operationFormattedPayload?.worker_name
              ? operationFormattedPayload?.worker_name
              : null,
            pipeline_id: copyOfNode?.pipeline_id,
          },
          documentation_detail: copyOfNode.data.documentation_detail,
          model_meta_data_detail: nodeData?.instances?.model_meta_data_detail,
        },
        node_id: nodeData?.instances?.node_id,
        node_display_name: copyOfNode?.node_display_name,
        node_name: nodeData?.node_name,
        project_id: activeProject?.project_id,
      };

      delete updatePayloadNormalOperation?.node_instance?.node_id;
      delete updatePayloadNormalOperation?.payload;
      delete updatePayloadNormalOperation?.documentation_detail;

      updateNode(
        nodeData?.node_job_type === 'normal'
          ? updatePayloadNormalOperation
          : updatePayloadConsole
      );
      // setNodeData((nodeData) => {
      //   const ndata = { ...nodeData };
      //   ndata.data.udfOperation.outputs = formikForm.values.outputs;
      //   ndata.data.udfOperation.args = formikForm.values.args;
      //   ndata.node_output = formikForm.values.outputs;
      //   return ndata;
      // });
    },
    [
      nodeData,
      updateNode,
      operationForm,
      operationFormattedPayload,
      selectedWorkerMetaData,
    ]
  );

  const onNormalOperationSubmit = (payload) => {
    setOperationFormattedPayload(payload);
  };
  const formValidationSchema = object({
    nodeDisplayName: string()
      .matches(REGEX.ALPHA_NUMERIC_UNDERSCORE_FIRST_CHAR, 'Incorrect input')
      .required('Required')
      .min(nodeDisplayNameMinLength, 'Please enter 2 or more characters.')
      .max(nodeDisplayNameMaxLength, 'Only 200 characters are allowed.'),
    opActiveDataset: object().nullable().required('Required'),
    cpu_limit: number().when(['isExecutedInRemoteWorker', 'showCPUFields'], {
      // eslint-disable-next-line no-shadow
      is: (isExecutedInRemoteWorker, showCPUFields) =>
        isExecutedInRemoteWorker && showCPUFields,
      then: number().min(1, 'Value must be greater than 0'),
    }),
    memory_limit: number().when(['isExecutedInRemoteWorker', 'showCPUFields'], {
      // eslint-disable-next-line no-shadow
      is: (isExecutedInRemoteWorker, showCPUFields) =>
        isExecutedInRemoteWorker && showCPUFields,
      then: number().min(1, 'Value must be greater than 0'),
    }),
  });

  const formikForm = useFormik({
    initialValues: {
      args: [],
      outputs: [],
      pod_spec: '',
      attachDoc: false,
      enableAddingModelMetaData: false,
      documentation_detail: [],
      modelMetaDataDetail: [],
      nodeDisplayName: '',
      opActiveDataset: {},
      worker_name: '',
      condaEnvironmentMetaData: {},
      cpu_limit: '',
      memory_limit: '',
    },
    validationSchema: formValidationSchema,
    // eslint-disable-next-line no-use-before-define
    onSubmit: onFormSubmit,
  });

  const formikForm1 = useFormik({
    initialValues: {
      outputName: '',
      outputType: '',
    },
    onSubmit: () => {
      formikForm.setFieldValue('outputs', [
        ...formikForm.values.outputs,
        {
          id: Date.now(),
          name: formikForm1.values.outputName,
          dataType: formikForm1.values.outputType,
        },
      ]);
      formikForm1.handleReset();
    },
  });

  useEffect(() => {
    if (activeProject?.project_id) {
      fetchAllModels(
        {
          project_id: activeProject?.project_id,
        },
        [
          'id',
          'model_name',
          'model_technique',
          'model_technique_label',
          'external_pkl',
          'train_table',
          'archive',
          'status',
          'is_logical',
        ].join()
      ).then(
        (resp) => {
          setAllModels(resp);
        },
        () => {
          setAllModels([]);
        }
      );
    }
  }, [activeProject]);

  useEffect(() => {
    if (
      pipelineData?.pipeline_setting?.doc_template_id !== -1 &&
      window.CONFIG.NEW_DOCUMENTATION === 'false'
    ) {
      fetchEditorOverview({
        docID: pipelineData?.pipeline_setting?.doc_template_id,
      });
    }
    if (
      window.CONFIG.NEW_DOCUMENTATION === 'true' &&
      pipelineData?.pipeline_setting?.doc_template_id !== -1
    ) {
      fetchPlaceholderList(
        pipelineData?.pipeline_setting?.doc_template_id
      ).then((resp) => {
        setPlaceholderList(resp?.data);
      });
    }
  }, [pipelineData]);

  const getXcomExpression = useCallback(
    (previousNode, selectedNode, _index) => {
      // case for intra-pipeline node to node I/O mapping
      if (
        previousNode?.extent === 'parent' &&
        selectedNode?.extent === 'parent' &&
        previousNode?.parentNodeName === selectedNode?.parentNodeName
      ) {
        return `{{ti.xcom_pull(task_ids='${selectedNode?.parentNodeName}.check_${previousNode?.node_name}',key='${previousNode?.node_name}_${_index}')}}`;
      }
      // case for pipeline to pipeline & Pipeline to node connection I/O mapping
      if (
        (previousNode?.extent === 'parent' &&
          selectedNode?.extent === 'parent' &&
          previousNode?.parentNodeName !== selectedNode?.parentNodeName) ||
        (previousNode?.extent === 'parent' && selectedNode?.extent !== 'parent')
      ) {
        return `{{ti.xcom_pull(task_ids='${previousNode?.parentNodeName}.check_${previousNode?.node_name}',key='${previousNode?.node_name}_${_index}')}}`;
      }
      // case for node to node & node to pipeline connection I/O mapping
      return `{{ti.xcom_pull(task_ids='check_${previousNode?.node_name}',key='${previousNode?.node_name}_${_index}')}}`;
    },
    [nodeData]
  );

  const [childPipelineArguments, setChildPipelineArguments] = useState([]);

  useEffect(() => {
    (async () => {
      const payloadAndModelsAndProjectAndDataset =
        nodeData?.instances?.payload &&
        Array.isArray(allModels) &&
        activeProject &&
        Array.isArray(datasetList);
      if (
        payloadAndModelsAndProjectAndDataset &&
        Array.isArray(consoleFileList) &&
        nodes &&
        edges
      ) {
        setAdditionalDatasetsFromPrevNode([]);
        setAdditionalModelsFromPrevNode([]);

        setAdditionalDatasetsFromPipelineArguments([]);
        setAdditionalModelFromPipelineArguments([]);
        setAdditionalConsoleFilesFromPipelineArguments([]);

        const incomingNodes = getIncomingNodeIds(nodeData, edges);
        const incomingNodesWithOutputs = nodes.filter(
          (item) => incomingNodes.includes(item.id) && item?.node_output?.length
        );
        const prepareAdditionalDatasetsFromPrevNode = [];
        const prepareaAddtionalModelsFromPrevNode = [];

        const datasetsFromPipelineArguments = [];
        const modelFromPipelineArguments = [];
        const consoleFileFromPipelineArguments = [];
        const pipelineArgumentsState = {
          String: [],
          Float: [],
          Int: [],
          Boolean: [],
          Dataset: [],
        };

        if (nodeData?.extent === 'parent') {
          const payload = {
            pipeline_id: nodeData?.pipeline_id,
            execution_id: nodeData?.executionId,
          };
          fetchConnectedPipelineArguemnts(payload).then((res) => {
            const childPipelineArgs = res?.data?.pipeline_arguments?.map(
              (item) => ({
                ...item,
                pipeline_id: nodeData?.pipeline_id,
                execution_id: nodeData?.executionId,
                parent_node_name: nodeData?.parentNodeDisplayName,
                parent_node_name_uuid: nodeData?.parentNodeName,
              })
            );
            setChildPipelineArguments(childPipelineArgs);
          });
        }

        const argumentsFromParentPipelines = [
          ...pipelineData?.pipeline_arguments,
          ...childPipelineArguments,
        ];

        argumentsFromParentPipelines?.forEach((ele) => {
          switch (ele.type) {
            case 'Dataset':
              // eslint-disable-next-line no-case-declarations
              const ds = datasetList.find(
                (dataset) => dataset.id === ele?.value
              );
              datasetsFromPipelineArguments.push({
                ...ds,
                ...(ele?.parent_node_name
                  ? { argumentsFromPipeline: true }
                  : { argumentsFromParentPipeline: true }),
                parentPipelineName: ele?.parent_node_name
                  ? ele?.parent_node_name
                  : pipelineData?.pipeline_display_name,
                table_display_name: ele?.name,
                dataset_id: ele?.parent_node_name_uuid
                  ? `pipeline_args(${ele.name}, ${ele.type},${ele?.parent_node_name_uuid})`
                  : `pipeline_args(${ele.name}, ${ele.type})`,
                id: ele?.value,
              });

              break;
            case 'Model':
              // eslint-disable-next-line no-case-declarations
              const md = allModels.find((model) => model.id === ele?.value);
              modelFromPipelineArguments.push({
                ...md,
                parentPipelineName: ele?.parent_node_name
                  ? ele?.parent_node_name
                  : pipelineData?.pipeline_display_name,
                modelId: ele?.parent_node_name_uuid
                  ? `pipeline_args(${ele.name}, ${ele.type},${ele?.parent_node_name_uuid})`
                  : `pipeline_args(${ele.name}, ${ele.type})`,
                model_name: ele?.name,
                ...(ele?.parent_node_name
                  ? { argumentsFromPipeline: true }
                  : { argumentsFromParentPipeline: true }),
                id: ele?.value,
              });
              break;
            case 'Column':
              // eslint-disable-next-line no-case-declarations
              const colDataset = datasetList.find(
                (dataset) => dataset?.id === ele?.dataset
              );
              if (colDataset) {
                pipelineArgumentsState.Dataset.push({
                  ...colDataset,
                  isColumnArg: true,
                  parentPipelineName: ele?.parent_node_name
                    ? ele?.parent_node_name
                    : pipelineData?.pipeline_display_name,
                  table_display_name: ele.name,
                  ...(ele?.parent_node_name
                    ? { argumentsFromPipeline: true }
                    : { argumentsFromParentPipeline: true }),
                  dataset_id: ele?.parent_node_name_uuid
                    ? `pipeline_args(${ele.name}, ${ele.type},${ele?.parent_node_name_uuid})`
                    : `pipeline_args(${ele.name}, ${ele.type})`,
                  value: ele.value,
                });
              }
              break;
            case 'ConsoleFiles':
              consoleFileFromPipelineArguments.push({
                ...ele,
                ...(ele?.parent_node_name
                  ? { argumentsFromPipeline: true }
                  : { argumentsFromParentPipeline: true }),
                parentPipelineName: ele?.parent_node_name
                  ? ele?.parent_node_name
                  : pipelineData?.pipeline_display_name,
                table_display_name: ele.name,
                id: ele.value.id,
                file_id: ele?.parent_node_name_uuid
                  ? `pipeline_args(${ele.name}, ${ele.type},${ele?.parent_node_name_uuid})`
                  : `pipeline_args(${ele.name}, ${ele.type})`,
              });
              break;

            case 'String':
              pipelineArgumentsState.String.push({
                ...ele,
                ...(ele?.parent_node_name
                  ? { argumentsFromPipeline: true }
                  : { argumentsFromParentPipeline: true }),
                parentPipelineName: ele?.parent_node_name
                  ? ele?.parent_node_name
                  : pipelineData?.pipeline_display_name,
                table_display_name: ele.name,
                value: ele?.parent_node_name_uuid
                  ? `pipeline_args(${ele.name}, ${ele.type},${ele?.parent_node_name_uuid})`
                  : `pipeline_args(${ele.name}, ${ele.type})`,
              });
              break;

            case 'Float':
              pipelineArgumentsState.Float.push({
                ...ele,
                ...(ele?.parent_node_name
                  ? { argumentsFromPipeline: true }
                  : { argumentsFromParentPipeline: true }),
                parentPipelineName: ele?.parent_node_name
                  ? ele?.parent_node_name
                  : pipelineData?.pipeline_display_name,
                table_display_name: ele.name,
                value: ele?.parent_node_name_uuid
                  ? `pipeline_args(${ele.name}, ${ele.type},${ele?.parent_node_name_uuid})`
                  : `pipeline_args(${ele.name}, ${ele.type})`,
              });
              break;

            case 'Int':
              pipelineArgumentsState.Int.push({
                ...ele,
                ...(ele?.parent_node_name
                  ? { argumentsFromPipeline: true }
                  : { argumentsFromParentPipeline: true }),
                parentPipelineName: ele?.parent_node_name
                  ? ele?.parent_node_name
                  : pipelineData?.pipeline_display_name,
                table_display_name: ele.name,
                value: ele?.parent_node_name_uuid
                  ? `pipeline_args(${ele.name}, ${ele.type},${ele?.parent_node_name_uuid})`
                  : `pipeline_args(${ele.name}, ${ele.type})`,
              });
              break;

            case 'Boolean':
              pipelineArgumentsState.Boolean.push({
                ...ele,
                ...(ele?.parent_node_name
                  ? { argumentsFromPipeline: true }
                  : { argumentsFromParentPipeline: true }),
                parentPipelineName: ele?.parent_node_name
                  ? ele?.parent_node_name
                  : pipelineData?.pipeline_display_name,
                table_display_name: ele.name,
                value: ele?.parent_node_name_uuid
                  ? `pipeline_args(${ele.name}, ${ele.type},${ele?.parent_node_name_uuid})`
                  : `pipeline_args(${ele.name}, ${ele.type})`,
              });
              break;
            default:
              break;
          }
        });
        incomingNodesWithOutputs?.forEach((node) => {
          if (node?.node_output?.length) {
            node?.node_output?.forEach((nodeOut, index) => {
              switch (nodeOut.type) {
                case 'dataset':
                  // eslint-disable-next-line no-case-declarations
                  const ds = datasetList.find(
                    (dataset) => dataset.id === nodeOut.key
                  );
                  prepareAdditionalDatasetsFromPrevNode.push({
                    ...ds,
                    dataset_id: getXcomExpression(node, nodeData, index),
                    table_display_name: nodeOut?.label,
                    prevNodeName: node.data?.label,
                    outputFromPrevNode: true,
                  });

                  break;
                case 'table':
                  break;
                case 'model':
                  // eslint-disable-next-line no-case-declarations
                  const md = allModels.find(
                    (model) => model.id === nodeOut.key
                  );
                  prepareaAddtionalModelsFromPrevNode.push({
                    model_name: nodeOut?.label,
                    ...md,
                    modelId: getXcomExpression(node, nodeData, index),
                    prevNodeName: node.data?.label,
                    outputFromPrevNode: true,
                  });
                  break;
                default:
                  break;
              }
            });
          }
        });

        setAdditionalDatasetsFromPipelineArguments(
          cloneDeep(datasetsFromPipelineArguments)
        );
        setAdditionalModelFromPipelineArguments(
          cloneDeep(modelFromPipelineArguments)
        );
        setAdditionalConsoleFilesFromPipelineArguments(
          cloneDeep(consoleFileFromPipelineArguments)
        );

        setAdditionalDatasetsFromPrevNode(
          cloneDeep(prepareAdditionalDatasetsFromPrevNode)
        );
        setAdditionalModelsFromPrevNode(
          cloneDeep(prepareaAddtionalModelsFromPrevNode)
        );

        setAdditionalPipelineArguments(cloneDeep(pipelineArgumentsState));

        if (nodeData?.node_job_type === 'consoleJobNode') {
          const newNodeArgData = [...nodeData?.instances?.payload?.args];
          const argsFromTaskResult = [...newNodeArgData];
          const udf = nodeData?.data?.udfDetails;

          // eslint-disable-next-line no-restricted-syntax
          for (const [index, arg] of argsFromTaskResult.entries()) {
            // eslint-disable-next-line no-useless-catch
            try {
              // eslint-disable-next-line no-await-in-loop
              argsFromTaskResult[index] = await formatArg(
                cloneDeep(arg),
                [
                  ...allModels,
                  ...cloneDeep(prepareaAddtionalModelsFromPrevNode),
                  ...cloneDeep(modelFromPipelineArguments),
                ],
                [
                  ...datasetList,
                  ...cloneDeep(prepareAdditionalDatasetsFromPrevNode),
                  ...cloneDeep(datasetsFromPipelineArguments),
                  ...cloneDeep(pipelineArgumentsState?.Dataset),
                ],
                [
                  ...consoleFileList,
                  ...cloneDeep(consoleFileFromPipelineArguments),
                ],
                fetchDatasetColumnDetails,
                activeProject,
                pipelineArgumentsState
              );
            } catch (error) {
              throw error;
            }
          }
          if (udf) {
            udf.args = [...argsFromTaskResult];
            formikForm.setFieldValue('args', argsFromTaskResult);
          }
        }

        formikForm.setFieldValue(
          'pod_spec',
          nodeData?.instances?.payload?.pod_spec
            ? nodeData?.instances?.payload?.pod_spec
            : 'low'
        );
        if (nodeData?.instances?.payload?.worker_name) {
          setIsExecutedInRemoteWorker(true);
          const defaultWorker = {
            worker_name: nodeData?.instances?.payload?.worker_name,
            meta_data: {
              use_conda: nodeData?.instances?.payload?.meta_data?.use_conda,
              envs: [
                {
                  name:
                    nodeData?.instances?.payload?.meta_data?.env?.name || '',
                  path:
                    nodeData?.instances?.payload?.meta_data?.env?.path || '',
                },
              ],
            },
          };
          const selectedWorker =
            allWorkersList?.find(
              (ele) =>
                ele?.worker_name === nodeData?.instances?.payload?.worker_name
            ) || defaultWorker;
          formikForm.setFieldValue('worker_name', selectedWorker || '');

          formikForm.setFieldValue(
            'cpu_limit',
            nodeData?.instances?.payload?.meta_data?.cpu_limit || ''
          );
          formikForm.setFieldValue(
            'memory_limit',
            nodeData?.instances?.payload?.meta_data?.memory_limit || ''
          );
          if (selectedWorker?.meta_data?.use_conda) {
            setIsCondaEnabled(true);
            setCondaEnvironments(
              selectedWorker?.meta_data?.envs?.map((env) => env?.name)
            );
            setSelectedWorkerMetaData(selectedWorker?.meta_data);

            if (nodeData?.instances?.payload?.meta_data?.env?.name) {
              formikForm.setFieldValue(
                'condaEnvironment',
                nodeData?.instances?.payload?.meta_data?.env.name
              );
              formikForm.setFieldValue(
                'condaEnvironmentMetaData',
                nodeData?.instances?.payload?.meta_data?.env
              );
            }
          }
        } else {
          setIsExecutedInRemoteWorker(false);
        }

        formikForm.setFieldValue(
          'nodeDisplayName',
          nodeData?.node_display_name ? nodeData?.node_display_name : ''
        );
        if (nodeData?.instances?.documentation_detail?.length > 0) {
          formikForm.setFieldValue('attachDoc', true);
          formikForm.setFieldValue(
            'documentation_detail',
            nodeData?.instances?.documentation_detail
          );
        }

        if (nodeData?.instances?.model_meta_data_detail?.length > 0) {
          formikForm.setFieldValue('enableAddingModelMetaData', true);
          formikForm.setFieldValue(
            'modelMetaDataDetail',
            nodeData?.instances?.model_meta_data_detail
          );
        }

        if (nodeData?.instances?.node_output?.length > 0) {
          formikForm.setFieldValue('outputs', nodeData?.instances?.node_output);
        }
        if (
          nodeData?.instances?.payload?.dataset_id ||
          nodeData?.instances?.payload?.dataset_id_one
        ) {
          const datasets = [
            ...datasetList,
            ...prepareAdditionalDatasetsFromPrevNode,
            ...datasetsFromPipelineArguments,
          ];

          const findDataset = (datasetId) => {
            return datasets.find((ele) => {
              return (
                ele?.id === datasetId ||
                ele?.id === Number(datasetId) ||
                ele?.dataset_id === datasetId ||
                (ele?.id &&
                  datasetId &&
                  utils.checkForIds(ele?.id, datasetId)) ||
                (ele?.dataset_id &&
                  Array.isArray(datasetId) &&
                  utils.checkForIds(ele?.dataset_id, datasetId))
              );
            });
          };

          let selectedDataset = null;

          if (nodeData?.instances?.payload?.dataset_id_one) {
            selectedDataset = findDataset(
              nodeData?.instances?.payload?.dataset_id_one
            );
          }

          if (!selectedDataset && nodeData?.instances?.payload?.dataset_id) {
            selectedDataset = findDataset(
              nodeData?.instances?.payload?.dataset_id
            );
          }

          formikForm.setFieldValue('opActiveDataset', selectedDataset);
        }
      }
    })();
  }, [
    nodeData,
    allModels,
    datasetList,
    activeProject,
    nodes,
    edges,
    consoleFileList,
  ]);

  /**
   * @function handleChange
   * @description function to handle change in formik form
   * @param {string} index index of the argument
   * @param {string} arg name of the argument
   * @param {object} event event object
   * @returns {undefined} undefined
   */
  const handleChange = useCallback(
    (index, _arg, event) => {
      const updatedArgs = Object.assign([], formikForm.values.args);
      updatedArgs[index] = {
        ...updatedArgs[index],
        value: event.target.value,
      };
      formikForm.setFieldValue('args', updatedArgs);
    },
    [formikForm]
  );

  /**
   * @function handleSelectChange
   * @description function to handle change in formik form
   * @param {string} index index of the argument
   * @param {string} arg name of the argument
   * @param {object} event object of the event fired
   * @param {object} value value of the selected option
   * @returns {undefined} undefined
   */
  const fetchSelectedDatasetColumnDetails = useCallback(
    (index, _arg, _event, value) => {
      const copyOfArgs = cloneDeep(formikForm.values.args);
      if (value?.id) {
        fetchDatasetColumnDetails({
          project_id: activeProject?.project_id,
          dataset_id: value?.id,
        }).then(
          (resp) => {
            copyOfArgs[index] = {
              ...copyOfArgs[index],
              datasetColumns: resp.data.columns,
              dataset: value,
              value: {},
            };
            formikForm.setFieldValue('args', copyOfArgs);
          },
          () => {
            copyOfArgs[index] = {
              ...copyOfArgs[index],
              datasetColumns: [],
              dataset: value,
              value: {},
            };
            formikForm.setFieldValue('args', copyOfArgs);
          }
        );
      } else {
        copyOfArgs[index] = {
          ...copyOfArgs[index],
          datasetColumns: [],
          dataset: value,
          value: {},
        };
        formikForm.setFieldValue('args', copyOfArgs);
      }
    },
    [activeProject, formikForm]
  );

  /**
   * @function updateOuputDocumentNodeMapping
   * @description function to update the output document node mapping
   * @param {object} output output object of the node
   * @param {string} propName name of the property
   * @param {string} propValue value of the property
   * @returns {undefined} undefined
   */
  const updateOuputDocumentNodeMapping = (output, propName, propValue) => {
    const copyOfDocDetails = clone(formikForm.values.documentation_detail);
    const index = copyOfDocDetails.findIndex(
      (item) => item?.payload?.actualId === output.key
    );
    if (index !== -1) {
      copyOfDocDetails[index].payload[propName] = propValue;
    } else {
      copyOfDocDetails.push(
        utils.prepareDocOutputMappingObj(
          {
            ...output,
            [propName]: propValue,
            index: formikForm.values.outputs.findIndex(
              (op) => op.key === output.key
            ),
          },
          nodeData,
          activeProject
        )
      );
    }
    formikForm.setFieldValue('documentation_detail', copyOfDocDetails);
  };

  /**
   * @function updateOuputModelMetaDataSchemaNodeMapping
   * @description function to update the output document node mapping
   * @param {object} output output object of the node
   * @param {string} propName name of the property
   * @param {string} propValue value of the property
   * @returns {undefined} undefined
   */
  const updateOuputModelMetaDataSchemaNodeMapping = (output, sec, attr) => {
    const copyOfDetails = clone(formikForm.values.modelMetaDataDetail);
    const index = copyOfDetails.findIndex(
      (item) => item?.payload?.actualId === output.key
    );
    if (index === -1) {
      const indexOfOutput = formikForm.values.outputs.findIndex(
        (op) => op.key === output.key
      );
      copyOfDetails.push({
        input: `${nodeData?.node_name}_${indexOfOutput}`,
        payload: {
          actualId: output.key,
          project_id: activeProject?.project_id,
          section_name: sec.section_name,
          attribute_name: attr.name,
          attribute_label: attr.label,
        },
        type: output.type,
        url: API_ENDPOINTS.MODULE_BASE_URL.ME + API_ENDPOINTS.ME.ADD_TO_MMD,
      });
    } else {
      copyOfDetails[index].payload.section_name = sec.section_name;
      copyOfDetails[index].payload.attribute_name = attr.name;
      copyOfDetails[index].payload.attribute_label = attr.label;
    }

    formikForm.setFieldValue('modelMetaDataDetail', copyOfDetails);
  };

  const changeAccordionState = (panel) => (_event, isExpanded) => {
    setAccordianState(isExpanded ? panel : false);
  };

  const opACtiveDatasetOnChange = useCallback(
    (_event, newValue) => {
      formikForm.setFieldValue('opActiveDataset', newValue || null);
      setNodeParameters({});
    },
    [formikForm, setNodeParameters]
  );

  const attachDocOnChange = useCallback(
    (_e, checked) => {
      formikForm.setFieldValue('attachDoc', checked);
      if (!checked) {
        formikForm.setFieldValue('documentation_detail', []);
      }
    },
    [formikForm]
  );

  const opActiveDatasetRenderInput = useCallback(
    (params) => (
      <TextField
        required
        {...params}
        label={
          nodeData?.data?.operationDetails?.operation?.key === 'Append'
            ? 'First dataset'
            : 'Dataset'
        }
        placeholder="Select dataset"
        InputLabelProps={{
          shrink: true,
        }}
        name="opActiveDataset"
        onBlur={formikForm.handleBlur}
        helperText={
          formikForm?.errors?.opActiveDataset &&
          formikForm?.touched?.opActiveDataset
            ? formikForm.errors.opActiveDataset
            : null
        }
        error={
          Boolean(formikForm?.errors?.opActiveDataset) &&
          formikForm?.touched?.opActiveDataset
        }
      />
    ),
    [formikForm]
  );

  const datasetRenderOption = useCallback(
    (props, option) => (
      <Box
        display="flex"
        flexGrow={1}
        alignItems="center"
        textOverflow="ellipsis"
        {...props}
      >
        <Box
          display="flex"
          flexGrow={1}
          textOverflow="ellipsis"
          width={10}
          pr={2}
        >
          <Typography noWrap>{option.table_display_name}</Typography>
        </Box>
        {/* for pipeline node */}
        {option.outputFromPrevNode ? (
          <Box display="flex" alignSelf="flex-end">
            <Typography
              variant="caption"
              noWrap
              width={175}
              title={option?.parentPipelineName}
              textOverflow="ellipsis"
            >
              Output from previous node : {option?.prevNodeName}
            </Typography>
          </Box>
        ) : (
          ''
        )}
        {/* for pipeline argument */}
        {option.argumentsFromPipeline ? (
          <Box display="flex" alignSelf="flex-end">
            <Typography
              variant="caption"
              noWrap
              width={175}
              title={option?.parentPipelineName}
              textOverflow="ellipsis"
            >
              Pipeline argument : {option?.parentPipelineName}
            </Typography>
          </Box>
        ) : (
          ''
        )}
        {/* for Parent pipeline arguments */}
        {option?.argumentsFromParentPipeline ? (
          <Box display="flex" alignSelf="flex-end">
            <Typography
              variant="caption"
              noWrap
              width={175}
              title={option?.parentPipelineName}
              textOverflow="ellipsis"
            >
              Parent pipeline argument : {option?.parentPipelineName}
            </Typography>
          </Box>
        ) : (
          ''
        )}
      </Box>
    ),
    []
  );

  const dialogOpenClose = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const submitOnClick = useCallback(() => {
    if (nodeData?.node_job_type === 'normal') {
      operationForm?.handleSubmit();
      setTimeout(() => {
        formikForm.handleSubmit();
      }, 1000);
    } else {
      let stop = false;
      const pipelineArgumentsNames = pipelineData?.pipeline_arguments?.map(
        (item) => item.name
      );
      formikForm?.values?.args.forEach((arg) => {
        const argName =
          typeof arg?.value === 'string'
            ? extractArgNameFromExpression(arg?.value)
            : arg?.value?.name;

        if (arg.type === 'Column' && !arg.value.length) {
          SwalToast({
            icon: 'error',
            title: 'Please fill all required arguments.',
          });
          stop = true;
        }
        if (
          arg.type !== 'Column' &&
          (arg.value === undefined || arg.value === '' || arg.value === null)
        ) {
          SwalToast({
            icon: 'error',
            title: 'Please fill all required arguments.',
          });
          stop = true;
        }
        // case for handeling the UFG form when parent pipeline args are mapped and editied
        if (
          argName &&
          argsTypesForValidationCheck.includes(arg?.type) &&
          !pipelineArgumentsNames.includes(argName)
        ) {
          SwalToast({
            icon: 'error',
            title: 'Please fill all required arguments.',
          });
          stop = true;
        }
      });
      if (stop) {
        return;
      }
      formikForm.handleSubmit();
    }
  }, [nodeData, operationForm, formikForm]);

  useEffect(() => {
    fetchPods().then((workerResponse) => {
      if (workerResponse?.data) {
        setAllWorkersList(workerResponse?.data);
      }
    });
    // }
  }, [fetchPods]);

  const workersRenderInput = (params) => (
    <TextField
      {...params}
      label="Worker"
      placeholder="Select worker"
      InputLabelProps={{
        shrink: true,
      }}
    />
  );

  const condaEnvironmentRenderInput = useCallback(
    (params) => (
      <TextField
        {...params}
        label="Conda environment"
        placeholder="Select conda environment"
        InputLabelProps={{ shrink: true }}
      />
    ),
    []
  );

  const workersRenderOption = (optionProps, el) => (
    <Box
      display="flex"
      flexGrow={1}
      alignItems="center"
      textOverflow="ellipsis"
      {...optionProps}
    >
      <Box display="flex" flexGrow={1} textOverflow="ellipsis" pr={2}>
        <Typography noWrap>{el?.worker_name}</Typography>
      </Box>
    </Box>
  );

  const isExecutedInRemoteWorkerOnChange = useCallback(() => {
    setIsExecutedInRemoteWorker(!isExecutedInRemoteWorker);
  }, [isExecutedInRemoteWorker]);

  useEffect(() => {
    if (!isExecutedInRemoteWorker) {
      formikForm.setFieldValue('worker_name', '');
      formikForm.setFieldValue('cpu_limit', '');
      formikForm.setFieldValue('memory_limit', '');
    }
  }, [isExecutedInRemoteWorker]);

  const renderPlaceholderInput = useCallback(
    (params) => {
      return (
        <TextField
          required
          {...params}
          name="placeholder"
          placeholder="Select placeholder"
          InputLabelProps={{
            shrink: true,
          }}
          onBlur={formikForm.handleBlur}
          helperText={
            formikForm?.errors?.placeholder?.id &&
            formikForm?.touched?.placeholder
              ? formikForm.errors.placeholder?.id
              : null
          }
          error={
            Boolean(formikForm?.errors?.placeholder?.id) &&
            formikForm?.touched?.placeholder
          }
        />
      );
    },
    [formikForm]
  );

  const placeholderEqualToValue = useCallback((option, val) => {
    return option?.id && option?.id === val?.id;
  }, []);

  const getOptionLabelPlaceholder = useCallback(
    (option) => {
      return option ? option.placeholder_name : '';
    },
    [placeholderList]
  );

  const headingKeyValue = useCallback(
    (op) => {
      let headingKey;
      if (window.CONFIG.NEW_DOCUMENTATION === 'false') {
        if (op.type === 'chart' || op.type === 'image') {
          headingKey = 'img_heading';
        } else {
          headingKey = 'table_heading';
        }
      } else {
        headingKey = 'heading';
      }
      return headingKey;
    },
    [window.CONFIG.NEW_DOCUMENTATION]
  );

  const deleteTitle = React.useMemo(() => {
    switch (true) {
      case readOnly === true:
        return 'Read-only mode';

      case activeProject?.is_completed === true:
        return PROJECT_COMPLETED;

      case deleteNodePermission !== 'Yes':
        return UNAUTHORIZED_MESSAGE;

      case nodeData?.extent === 'parent':
        return 'Deleting a child node is not permitted.';

      default:
        return 'Delete node.';
    }
  }, [deleteNodePermission, nodeData?.extent, activeProject?.is_completed]);

  const handleWorkerChange = useCallback(
    (_event, value) => {
      const workerName = value?.worker_name || '';
      formikForm.setFieldValue('worker_name', value || { worker_name: '' });
      formikForm.setFieldValue('condaEnvironment', '');

      const selectedWorker = allWorkersList?.find(
        (worker) => worker?.worker_name === workerName
      );
      if (selectedWorker?.meta_data) {
        setSelectedWorkerMetaData(selectedWorker?.meta_data);
      }

      if (selectedWorker?.meta_data?.use_conda) {
        setIsCondaEnabled(true);
        setCondaEnvironments(
          selectedWorker?.meta_data?.envs.map((env) => env.name)
        );
      } else {
        setIsCondaEnabled(false);
        setCondaEnvironments([]);
        setSelectedWorkerMetaData(null);
      }
    },
    [formikForm]
  );

  const updateTitle = React.useMemo(() => {
    switch (true) {
      case readOnly === true:
        return 'Read-only mode';

      case activeProject?.is_completed === true:
        return PROJECT_COMPLETED;

      case updateNodePermission !== 'Yes':
        return UNAUTHORIZED_MESSAGE;

      case nodeData?.extent === 'parent':
        return 'Editing a child node is not permitted.';

      default:
        return 'Update node.';
    }
  }, [updateNodePermission, nodeData?.extent, activeProject?.is_completed]);

  const opActiveDatastetIsOptionEqualToValue = useCallback((option, val) => {
    if (val?.dataset_id && option?.dataset_id) {
      return option?.dataset_id === val?.dataset_id;
    }
    if ((val?.dataset_id && option?.id) || (val?.id && option?.dataset_id)) {
      return false;
    }
    return option?.id === val?.id;
  }, []);

  const isDatasetOptionEqualToValue = (option, val) => {
    if (val?.dataset_id && option?.dataset_id) {
      return option?.dataset_id === val?.dataset_id;
    }
    if ((val?.dataset_id && option?.id) || (val?.id && option?.dataset_id)) {
      return false;
    }
    return option?.id === val?.id;
  };

  const isOptionEqualToValueWorker = useCallback((option, val) => {
    return option?.worker_name === val?.worker_name;
  }, []);

  const handleCpuLimitChange = (event) => {
    formikForm.setFieldValue('cpu_limit', event.target.value);
  };

  const handleMemoryLimitChange = (event) => {
    formikForm.setFieldValue('memory_limit', event.target.value);
  };

  return (
    <Dialog
      onClose={dialogOpenClose}
      aria-labelledby="customized-dialog-title"
      maxWidth="lg"
      fullWidth={true}
      open={open}
      TransitionComponent={DialogTransition}
    >
      <Box>
        <Box
          mr={4}
          sx={{
            wordWrap: 'break-word',
          }}
        >
          <DialogTitle display="flex" alignItems="center">
            <Typography
              variant="subtitle1"
              overflow="hidden"
              textOverflow="ellipsis"
              noWrap
              title={`Pipeline name - ${pipelineData?.pipeline_display_name} \nNode name - ${nodeData?.node_display_name} `}
              maxWidth="100%"
            >
              Edit : {pipelineData?.pipeline_display_name} /
              {nodeData?.node_display_name}
            </Typography>
          </DialogTitle>
        </Box>
        <IconButton
          onClick={dialogOpenClose}
          size="large"
          sx={DIALOG_ICON_BUTTON_CSS}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent dividers>
        <Grid item pb={2} xs={12}>
          <Typography
            variant="body4"
            tabIndex={0}
            role="note"
            aria-label="Important note: Fields marked with an asterisk are mandatory."
            sx={{
              fontStyle: 'normal',
              border: 'none',
              '&:focus': {
                border: '2px solid',
                borderColor: 'primary.main',
              },
            }}
          >
            <strong>Note:</strong>&nbsp;
            <i>Fields marked with * are mandatory.</i>
          </Typography>
        </Grid>
        <Accordion
          expanded={accordianState === 'panel1'}
          onChange={changeAccordionState('panel1')}
          disableGutters
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
          >
            <Typography sx={{ width: '33%', flexShrink: 0 }}>
              Node details
            </Typography>
            {accordianState !== 'panel1' ? (
              <Typography sx={{ color: typographyColor }} variant="body2">
                Details of the node.
              </Typography>
            ) : (
              ''
            )}
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction="column" spacing={3}>
              <Grid container xs={12} alignItems="center">
                <Grid item xs={4}>
                  <TextField
                    required
                    name="nodeDisplayName"
                    label="Node name"
                    placeholder="Enter node name"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={formikForm?.values?.nodeDisplayName}
                    onChange={(e) => {
                      formikForm.handleChange(e);
                    }}
                    onBlur={formikForm.handleBlur('nodeDisplayName')}
                    fullWidth
                    helperText={
                      formikForm?.errors?.nodeDisplayName &&
                      formikForm?.touched?.nodeDisplayName
                        ? formikForm.errors.nodeDisplayName
                        : null
                    }
                    error={
                      Boolean(formikForm?.errors?.nodeDisplayName) &&
                      formikForm?.touched?.nodeDisplayName
                    }
                    disabled={nodeData?.extent === 'parent' || readOnly}
                    InputProps={{
                      endAdornment: (
                        <Tooltip
                          position="end"
                          id="nodeDisplayName"
                          tabIndex={0}
                          enterDelay={0}
                          aria-label="Information"
                          title="Please use alphabets (a-z, A-Z) or numbers (0–9) or underscores (_) only. First letter should be an alphabet."
                          placement="bottom"
                          arrow
                        >
                          <IconButton aria-describedby="nodeDisplayName">
                            <InfoIcon color="primary" />
                          </IconButton>
                        </Tooltip>
                      ),
                    }}
                  />
                </Grid>
                {nodeData?.node_job_type === 'consoleJobNode' ? (
                  <Grid item xs={7} ml={3} mt={2} tabIndex={0}>
                    <Typography variant="body1">UDF name : </Typography>
                    <Typography variant="body2">
                      {nodeData?.data?.udfDetails?.name}
                    </Typography>
                    <br />
                  </Grid>
                ) : (
                  <Grid item xs={7} ml={3} mt={2} tabIndex={0}>
                    <Typography variant="body1">Operation name : </Typography>
                    <Typography variant="body2">
                      {nodeData?.data?.operationDetails?.operation?.label}
                    </Typography>
                    <br />
                    <Typography variant="body1">Location: </Typography>
                    <Typography variant="body2">
                      {nodeData?.data?.operationDetails?.moduleObjectKey} /{' '}
                      {nodeData?.data?.operationDetails?.submoduleObjectKey}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              <Grid item xs={12}>
                {confirmNodeDelete ? (
                  <Box>
                    <Typography tabIndex={0}>
                      Are you sure you want to delete?
                    </Typography>
                    <Box mt={1}>
                      <Button
                        style={{
                          width: '32px',
                          height: '32px',
                          fontSize: '13px',
                        }}
                        variant="outlined"
                        onClick={() => {
                          setConfirmNodeDelete(false);
                        }}
                        title="No"
                        aria-label="No"
                      >
                        No
                      </Button>
                      <Button
                        style={{
                          width: '32px',
                          height: '32px',
                          fontSize: '13px',
                          marginLeft: '10px',
                        }}
                        color="primary"
                        variant="contained"
                        onClick={() => {
                          deleteNode(
                            pipelineData?.pipeline_id,
                            nodeData?.instances?.node_id,
                            activeProject?.project_id
                          ).then(dialogOpenClose, () => {});
                        }}
                        title="Yes"
                        aria-label="Yes"
                      >
                        Yes
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Button
                    startIcon={
                      <DeleteIcon
                        color={nodeData?.extent && theme.palette.text.disabled}
                      />
                    }
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setConfirmNodeDelete(true);
                    }}
                    disabled={
                      deleteNodePermission !== 'Yes' ||
                      nodeData?.extent === 'parent' ||
                      readOnly
                    }
                    title={deleteTitle}
                    aria-label={deleteTitle}
                  >
                    <Typography
                      variant="body4"
                      color={
                        nodeData?.extent === 'parent'
                          ? theme.palette.text.disabled
                          : 'inherit'
                      }
                    >
                      Delete Node
                    </Typography>
                  </Button>
                )}
              </Grid>
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Divider flexItem />
        <Accordion
          expanded={accordianState === 'panel2'}
          onChange={changeAccordionState('panel2')}
          disableGutters
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
          >
            <Typography sx={{ width: '33%', flexShrink: 0 }}>
              {nodeData?.node_job_type === 'consoleJobNode'
                ? 'UDF'
                : 'Operation'}{' '}
              form
            </Typography>
            {accordianState !== 'panel2' ? (
              <Typography sx={{ color: typographyColor }} variant="body2">
                Fill all the details needed to run the node.
              </Typography>
            ) : (
              ''
            )}
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction="column" spacing={3}>
              {nodeData?.node_job_type === 'normal' &&
                nodeData?.data?.operationDetails?.operation?.key !==
                  'CombineDatasets' &&
                nodeData?.data?.operationDetails?.operation?.key !==
                  'DataDrift' && (
                  <Grid container>
                    <Grid item xs={4}>
                      <Autocomplete
                        name="opActiveDataset"
                        options={[
                          ...additionalDatasetsFromPipelineArguments,
                          ...additionalDatasetsFromPrevNode,
                          ...datasetList.filter((data) => {
                            const isAppendOperation =
                              nodeData?.data?.operationDetails?.operation
                                ?.key === 'Append';
                            return isAppendOperation
                              ? data?.published !== undefined
                              : data?.published === true;
                          }),
                        ]}
                        value={formikForm.values.opActiveDataset}
                        disableClearable={[null, undefined].includes(
                          formikForm.values.opActiveDataset
                        )}
                        noOptionsText="No dataset available."
                        getOptionLabel={opActiveDatasetGetOptionLabel}
                        popupIcon={<DropdownArrow />}
                        onChange={opACtiveDatasetOnChange}
                        isOptionEqualToValue={
                          opActiveDatastetIsOptionEqualToValue
                        }
                        renderOption={(props, option, index) => (
                          <Box
                            display="flex"
                            flexGrow={1}
                            alignItems="center"
                            textOverflow="ellipsis"
                            {...props}
                            key={index}
                          >
                            <Box
                              display="flex"
                              flexGrow={1}
                              textOverflow="ellipsis"
                              width={10}
                              pr={2}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  whiteSpace: 'nowrap',
                                  width: 165,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                                title={option.table_display_name}
                              >
                                {option.table_display_name}
                              </Typography>
                            </Box>
                            {/* for pipeline grument */}
                            {option.argumentsFromParentPipeline ? (
                              <Box display="flex" alignSelf="flex-end">
                                <Typography
                                  variant="caption"
                                  mb={0.2}
                                  noWrap
                                  width={175}
                                  title={option?.parentPipelineName}
                                  textOverflow="ellipsis"
                                >
                                  Parent pipeline argument :{' '}
                                  {option?.parentPipelineName}
                                </Typography>
                              </Box>
                            ) : (
                              ''
                            )}
                            {/* for pipeline grument */}
                            {option.argumentsFromPipeline ? (
                              <Box display="flex" alignSelf="flex-end">
                                <Typography
                                  variant="caption"
                                  mb={0.2}
                                  noWrap
                                  width={175}
                                  title={option?.parentPipelineName}
                                  textOverflow="ellipsis"
                                >
                                  Pipeline argument :{' '}
                                  {option?.parentPipelineName}
                                </Typography>
                              </Box>
                            ) : (
                              ''
                            )}
                            {/* for pipeline node */}
                            {option.outputFromPrevNode ? (
                              <Box display="flex" alignSelf="flex-end">
                                <Typography
                                  variant="caption"
                                  mb={0.2}
                                  noWrap
                                  width={175}
                                  title={option?.prevNodeName}
                                  textOverflow="ellipsis"
                                >
                                  Output from previous node :{' '}
                                  {option?.prevNodeName}
                                </Typography>
                              </Box>
                            ) : (
                              ''
                            )}
                          </Box>
                        )}
                        renderInput={opActiveDatasetRenderInput}
                      />
                    </Grid>
                  </Grid>
                )}
              {nodeData?.node_job_type === 'consoleJobNode' ? (
                <>
                  {formikForm?.values?.args?.length ? (
                    <Grid container direction="column">
                      <Grid item xs={12}>
                        <Box mt={2} />
                        {formikForm?.values?.args?.map((arg, index) => (
                          <Grid
                            container
                            alignItems="center"
                            style={{ marginBottom: 16, marginLeft: 5 }}
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                          >
                            <Grid item xs={6}>
                              {arg?.type === 'Column' ? (
                                <>
                                  <Box mb={3}>
                                    <Autocomplete
                                      popupIcon={<DropdownArrow />}
                                      options={[
                                        ...additionalDatasetsFromPrevNode,
                                        ...additionalPipelineArguments?.Dataset,
                                        ...datasetList.filter(
                                          (data) => data?.published === true
                                        ),
                                      ]}
                                      getOptionLabel={datasetGetOptionLabel}
                                      value={arg.dataset ? arg.dataset : {}}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label={`${arg?.name} (${arg.type})*`}
                                          name="dataset"
                                          placeholder="Select dataset name"
                                          InputLabelProps={{
                                            shrink: true,
                                          }}
                                        />
                                      )}
                                      isOptionEqualToValue={
                                        isDatasetOptionEqualToValue
                                      }
                                      renderOption={datasetRenderOption}
                                      // eslint-disable-next-line react/jsx-no-bind
                                      onChange={fetchSelectedDatasetColumnDetails.bind(
                                        this,
                                        index,
                                        arg
                                      )}
                                    />
                                  </Box>
                                  <Box>
                                    <ConsoleArgumentInput
                                      type={arg.type}
                                      datasetColumns={arg.datasetColumns}
                                      models={[
                                        ...additionalModelFromPipelineArguments,
                                        ...additionalModelsFromPrevNode,
                                        ...allModels,
                                      ]}
                                      additionalDatasetFromPrevNode={[
                                        ...additionalDatasetsFromPrevNode,
                                      ]}
                                      allDatasets={[
                                        ...additionalDatasetsFromPipelineArguments,
                                        ...additionalDatasetsFromPrevNode,
                                        ...datasetList.filter(
                                          (data) => data?.published === true
                                        ),
                                      ]}
                                      consoleFilesList={consoleFileList}
                                      additionalPipelineArguments={
                                        additionalPipelineArguments
                                      }
                                      activeProject={activeProject}
                                      value={arg.value}
                                      // eslint-disable-next-line react/jsx-no-bind
                                      onChange={handleChange.bind(
                                        this,
                                        index,
                                        arg
                                      )}
                                      placeholder={
                                        !arg?.value?.length
                                          ? utils.getPlaceholderTextByArgType(
                                              arg.type
                                            )
                                          : ''
                                      }
                                      argValue={arg}
                                      argIndex={index}
                                    />
                                  </Box>
                                </>
                              ) : (
                                <>
                                  <ConsoleArgumentInput
                                    type={arg.type}
                                    datasetColumns={arg.datasetColumns}
                                    models={[
                                      ...additionalModelFromPipelineArguments,
                                      ...additionalModelsFromPrevNode,
                                      ...allModels,
                                    ]}
                                    allDatasets={[
                                      ...additionalDatasetsFromPipelineArguments,
                                      ...additionalDatasetsFromPrevNode,
                                      ...datasetList.filter(
                                        (data) => data?.published === true
                                      ),
                                    ]}
                                    consoleFilesList={consoleFileList}
                                    additionalPipelineArguments={
                                      additionalPipelineArguments
                                    }
                                    activeProject={activeProject}
                                    value={arg.value}
                                    // eslint-disable-next-line react/jsx-no-bind
                                    onChange={handleChange.bind(
                                      this,
                                      index,
                                      arg
                                    )}
                                    placeholder={
                                      !arg?.value?.length
                                        ? utils.getPlaceholderTextByArgType(
                                            arg.type
                                          )
                                        : ''
                                    }
                                    label={`${arg?.name} (${arg.type})*`}
                                    additionalConsoleFilesFromPipelineArguments={
                                      additionalConsoleFilesFromPipelineArguments
                                    }
                                    argValue={arg}
                                    argIndex={index}
                                    form={formikForm}
                                  />
                                </>
                              )}
                            </Grid>
                          </Grid>
                        ))}
                        {/* Hiding the CPU configs when worker is selected */}
                        {!isExecutedInRemoteWorker ? (
                          <Grid item xs={3} mt={5} ml={0.5}>
                            <DropDown
                              name="pod_spec"
                              label="CPU specification"
                              placeholder="Select CPU specification"
                              value={formikForm.values?.pod_spec}
                              onChange={formikForm.handleChange}
                            >
                              {PodSpecs.map((el) => (
                                <DropdownItem key={el.value} value={el?.value}>
                                  {el?.label}
                                </DropdownItem>
                              ))}
                            </DropDown>
                          </Grid>
                        ) : null}
                        <Grid item sm={12} mt={1}>
                          <Switch
                            checked={isExecutedInRemoteWorker}
                            onChange={isExecutedInRemoteWorkerOnChange}
                          />
                          <Typography variant="body2">
                            Execute in remote worker.
                          </Typography>
                        </Grid>
                        {isExecutedInRemoteWorker ? (
                          <Grid>
                            <Grid item xs={3} mt={5} ml={0.5}>
                              <Autocomplete
                                popupIcon={<DropdownArrow />}
                                disableClearable
                                id="worker-list"
                                name="worker_name"
                                noOptionsText="No worker available."
                                value={formikForm?.values?.worker_name}
                                onChange={handleWorkerChange}
                                getOptionLabel={(option) =>
                                  option.worker_name ? option.worker_name : ''
                                }
                                options={allWorkersList}
                                renderInput={workersRenderInput}
                                renderOption={workersRenderOption}
                                isOptionEqualToValues={
                                  isOptionEqualToValueWorker
                                }
                              />
                            </Grid>
                          </Grid>
                        ) : null}
                        {isExecutedInRemoteWorker && (
                          <Grid
                            item
                            xs={3}
                            mt={2}
                            ml={0.5}
                            display="flex"
                            direction="row"
                          >
                            <Grid item xs={6}>
                              <TextField
                                label="CPU (Cores)"
                                name="cpu_limit"
                                placeholder="Enter CPU (Cores)"
                                value={formikForm.values.cpu_limit}
                                onChange={handleCpuLimitChange}
                                type="number"
                                error={
                                  Boolean(formikForm.errors.cpu_limit) &&
                                  formikForm?.touched?.cpu_limit
                                }
                                helperText={
                                  formikForm.errors.cpu_limit &&
                                  formikForm.touched.cpu_limit
                                    ? formikForm.errors.cpu_limit
                                    : null
                                }
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={6} ml={0.5}>
                              <TextField
                                label="RAM (GBs)"
                                name="memory_limit"
                                placeholder="Enter RAM (GBs)"
                                value={formikForm.values.memory_limit}
                                onChange={handleMemoryLimitChange}
                                type="number"
                                error={
                                  Boolean(formikForm.errors.memory_limit) &&
                                  formikForm?.touched?.memory_limit
                                }
                                helperText={
                                  formikForm.errors.memory_limit &&
                                  formikForm.touched.memory_limit
                                    ? formikForm.errors.memory_limit
                                    : null
                                }
                                fullWidth
                              />
                            </Grid>
                          </Grid>
                        )}
                        {isExecutedInRemoteWorker && isCondaEnabled && (
                          <Grid>
                            <Grid item xs={3} mt={5} ml={0.5} mb={1}>
                              <Autocomplete
                                popupIcon={<DropdownArrow />}
                                disableClearable
                                id="conda-env-list"
                                noOptionsText="No conda environments available"
                                value={formikForm.values.condaEnvironment}
                                onChange={(_event, value) =>
                                  formikForm.setFieldValue(
                                    'condaEnvironment',
                                    value
                                  )
                                }
                                options={condaEnvironments}
                                renderInput={condaEnvironmentRenderInput}
                              />
                            </Grid>
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <Typography variant="body4">
                            <strong>Note:</strong>&nbsp;
                            <i>
                              If input from the previous step is not displayed
                              here, make sure you have saved the pipeline after
                              adding a new connection.
                            </i>
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container display="flex" flexDirection="column">
                      <Box mt={2} mb={2}>
                        <Typography>
                          No argument are required for selected function.
                          <br />
                          Please go ahead and click on Submit.
                        </Typography>
                      </Box>
                      {!isExecutedInRemoteWorker ? (
                        <Grid item xs={3}>
                          <DropDown
                            name="pod_spec"
                            label="CPU specification"
                            placeholder="Select CPU specification"
                            value={formikForm.values?.pod_spec}
                            onChange={formikForm.handleChange}
                          >
                            {PodSpecs.map((el) => (
                              <DropdownItem key={el.value} value={el?.value}>
                                {el?.label}
                              </DropdownItem>
                            ))}
                          </DropDown>
                        </Grid>
                      ) : null}
                      <Grid item sm={12} mt={1}>
                        <Switch
                          checked={isExecutedInRemoteWorker}
                          onChange={isExecutedInRemoteWorkerOnChange}
                        />
                        <Typography variant="body2">
                          Execute in remote worker.
                        </Typography>
                      </Grid>

                      {isExecutedInRemoteWorker ? (
                        <Grid>
                          <Grid item xs={3} mt={5} ml={0.5}>
                            <Autocomplete
                              popupIcon={<DropdownArrow />}
                              disableClearable
                              id="worker-list"
                              name="worker_name"
                              noOptionsText="No worker available."
                              value={formikForm.values?.worker_name}
                              onChange={handleWorkerChange}
                              getOptionLabel={(option) =>
                                option.worker_name ? option.worker_name : ''
                              }
                              options={allWorkersList}
                              renderInput={workersRenderInput}
                              renderOption={workersRenderOption}
                              isOptionEqualToValues={isOptionEqualToValueWorker}
                            />
                          </Grid>
                        </Grid>
                      ) : null}
                      {isExecutedInRemoteWorker && (
                        <Grid
                          item
                          xs={3}
                          mt={2}
                          ml={0.5}
                          display="flex"
                          direction="row"
                        >
                          <Grid item xs={6}>
                            <TextField
                              label="CPU (Cores)"
                              name="cpu_limit"
                              placeholder="Enter CPU (Cores)"
                              value={formikForm.values.cpu_limit}
                              onChange={handleCpuLimitChange}
                              type="number"
                              error={
                                Boolean(formikForm.errors.cpu_limit) &&
                                formikForm?.touched?.cpu_limit
                              }
                              helperText={
                                formikForm.errors.cpu_limit &&
                                formikForm.touched.cpu_limit
                                  ? formikForm.errors.cpu_limit
                                  : null
                              }
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={6} ml={0.5}>
                            <TextField
                              label="RAM (GBs)"
                              name="memory_limit"
                              placeholder="Enter RAM (GBs)"
                              value={formikForm.values.memory_limit}
                              onChange={handleMemoryLimitChange}
                              type="number"
                              error={
                                Boolean(formikForm.errors.memory_limit) &&
                                formikForm?.touched?.memory_limit
                              }
                              helperText={
                                formikForm.errors.memory_limit &&
                                formikForm.touched.memory_limit
                                  ? formikForm.errors.memory_limit
                                  : null
                              }
                              fullWidth
                            />
                          </Grid>
                        </Grid>
                      )}
                      {isExecutedInRemoteWorker && isCondaEnabled && (
                        <Grid>
                          <Grid item xs={3} mt={5} ml={0.5} mb={1}>
                            <Autocomplete
                              popupIcon={<DropdownArrow />}
                              disableClearable
                              id="conda-env-list"
                              noOptionsText="No conda environments available"
                              value={formikForm.values.condaEnvironment}
                              onChange={(_event, value) =>
                                formikForm.setFieldValue(
                                  'condaEnvironment',
                                  value
                                )
                              }
                              options={condaEnvironments}
                              renderInput={condaEnvironmentRenderInput}
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </>
              ) : (
                <Grid>
                  <NormalOperationRenderer
                    moduleKey={nodeData?.module}
                    subModuleKey={nodeData?.submodule}
                    operationKey={
                      nodeData?.data?.operationDetails?.operation?.key
                    }
                    activeDataset={formikForm?.values?.opActiveDataset}
                    moduleObjKey={
                      nodeData?.data?.operationDetails?.moduleObjectKey
                    }
                    subModuleObjKey={
                      nodeData?.data?.operationDetails?.submoduleObjectKey
                    }
                    operationObjKey={
                      nodeData?.data?.operationDetails?.operation?.objKey
                    }
                    activeProject={activeProject}
                    permissions={allPermissions}
                    parameters={nodeParameters}
                    isPipeline={true}
                    getOperaionForm={(form) => {
                      setOperationForm(form);
                    }}
                    onNormalOperationSubmit={onNormalOperationSubmit}
                    taskDetails={nodeData?.instances?.task_details}
                    additionalModelsFromPrevNode={[
                      ...additionalModelsFromPrevNode,
                      ...additionalModelFromPipelineArguments,
                    ]}
                    // additionalModelFromPipelineArguments={
                    //   additionalModelFromPipelineArguments
                    // }
                    additionalDatasetFromPrevNode={[
                      ...additionalDatasetsFromPrevNode,
                      ...additionalDatasetsFromPipelineArguments,
                    ]}
                    // additionalDatasetsFromPipelineArguments={
                    //   additionalDatasetsFromPipelineArguments
                    // }
                    allWorkersList={allWorkersList}
                  />
                </Grid>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Divider flexItem />
        <Accordion
          expanded={accordianState === 'panel3'}
          onChange={changeAccordionState('panel3')}
          disableGutters
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
          >
            <Typography sx={{ width: '33%', flexShrink: 0 }}>
              Declared output
            </Typography>
            {accordianState !== 'panel3' ? (
              <Typography sx={{ color: typographyColor }} variant="body2">
                List of output items generated after successful execution of the
                current node.
              </Typography>
            ) : (
              ''
            )}
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction="column" spacing={3}>
              <Grid item xs={12}>
                <Box maxHeight={200} overflow="scroll">
                  {formikForm?.values?.outputs?.length ? (
                    formikForm?.values?.outputs?.map((output, i) => {
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <Box key={i} m={0} p={0}>
                          <DisplayOutput output={output} />
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="body2">Not output declared</Typography>
                  )}
                </Box>
              </Grid>
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Divider flexItem />
        <Accordion
          expanded={accordianState === 'panel4'}
          onChange={changeAccordionState('panel4')}
          disableGutters
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
          >
            <Typography sx={{ width: '33%', flexShrink: 0 }}>
              Declared output document mapping
            </Typography>
            {accordianState !== 'panel4' ? (
              <Typography sx={{ color: typographyColor }} variant="body2">
                Auto add output to document.
              </Typography>
            ) : (
              ''
            )}
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction="column" spacing={3}>
              <span>
                <Typography>Enable</Typography>
                <Switch
                  name="attachDoc"
                  disabled={
                    !pipelineData?.pipeline_setting?.enable_documentation
                  }
                  inputProps={{
                    title: pipelineData?.pipeline_setting?.enable_documentation
                      ? 'Attach a document.'
                      : 'Please select the document template first under the pipeline settings.',
                    role: 'switch',
                  }}
                  checked={formikForm.values.attachDoc === true}
                  onChange={attachDocOnChange}
                />
              </span>
              {formikForm.values.outputs.find((data) =>
                outputTypesCanbeAddedToDoc.includes(data.type)
              ) && (
                <Stack
                  maxHeight={300}
                  sx={{
                    overflowY: 'auto',
                    overflowX: 'hidden',
                  }}
                  spacing={1}
                >
                  {formikForm?.values?.attachDoc ? (
                    <Grid item container xs={12} spacing={1} ml={0.2}>
                      <Grid item xs={1.8}>
                        <Typography color="primary" variant="h6" tabIndex={0}>
                          Ouput name
                        </Typography>
                      </Grid>
                      <Grid item xs={1.2}>
                        <Typography color="primary" variant="h6" tabIndex={0}>
                          Output type
                        </Typography>
                      </Grid>

                      <Grid item xs={3}>
                        <Typography color="primary" variant="h6" tabIndex={0}>
                          Heading
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography color="primary" variant="h6" tabIndex={0}>
                          Comment
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        {window.CONFIG.NEW_DOCUMENTATION === 'true' ? (
                          <Typography color="primary" variant="h6" tabIndex={0}>
                            Document placeholder mapping
                          </Typography>
                        ) : (
                          <Typography color="primary" variant="h6" tabIndex={0}>
                            Mapped document node
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  ) : null}

                  {formikForm?.values?.attachDoc
                    ? formikForm?.values?.outputs
                        ?.filter((op) =>
                          outputTypesCanbeAddedToDoc.includes(op.type)
                        )
                        .map((op) => {
                          const data =
                            formikForm?.values?.documentation_detail?.filter(
                              (op1) => op1?.payload?.actualId === op.key
                            );
                          return (
                            <Grid
                              item
                              container
                              xs={12}
                              key={op?.key}
                              alignItems="center"
                              spacing={1}
                              pr={2}
                            >
                              <Grid
                                item
                                xs={1.8}
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                <Typography
                                  color="primary"
                                  variant="body1"
                                  title={op?.label}
                                  tabIndex={0}
                                >
                                  {op?.label}
                                </Typography>
                              </Grid>
                              <Grid
                                item
                                xs={1.2}
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  textTransform: 'capitalize',
                                }}
                              >
                                <Typography
                                  color="primary"
                                  variant="body1"
                                  textTransform="capitalize"
                                  title={op?.type}
                                  tabIndex={0}
                                >
                                  {op?.type === 'test_case'
                                    ? 'Test Case'
                                    : op?.type}
                                </Typography>
                              </Grid>
                              <Grid item xs={3}>
                                <TextField
                                  name="heading"
                                  placeholder="Enter heading"
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                  value={
                                    data[0]?.payload?.[headingKeyValue(op)]
                                  }
                                  onChange={(e) => {
                                    const headingkey = headingKeyValue(op);
                                    updateOuputDocumentNodeMapping(
                                      op,
                                      headingkey,
                                      e.target.value
                                    );
                                  }}
                                />
                              </Grid>
                              <Grid item xs={3}>
                                <TextField
                                  name="comment"
                                  placeholder="Enter comment"
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                  value={
                                    window.CONFIG.NEW_DOCUMENTATION === 'false'
                                      ? data[0]?.payload?.comment
                                      : data[0]?.payload?.description
                                  }
                                  onChange={(e) => {
                                    updateOuputDocumentNodeMapping(
                                      op,
                                      window.CONFIG.NEW_DOCUMENTATION ===
                                        'false'
                                        ? 'comment'
                                        : 'description',
                                      e.target.value
                                    );
                                  }}
                                />
                              </Grid>
                              <Grid item xs={3}>
                                {window.CONFIG.NEW_DOCUMENTATION === 'false' ? (
                                  <DocTreeDropdown
                                    docEditorOverview={docEditorOverview}
                                    label="Document node"
                                    placeholder="Select document node"
                                    defaultValue={
                                      data[0]?.payload?.[
                                        op.type === 'table'
                                          ? 'table_id'
                                          : op.type === 'chart' ||
                                              op.type === 'image'
                                            ? 'img_id'
                                            : 'test_case_id'
                                      ]
                                    }
                                    onNodeSelect={(val) => {
                                      updateOuputDocumentNodeMapping(
                                        op,
                                        op?.type === 'table'
                                          ? 'table_id'
                                          : op.type === 'chart' ||
                                              op.type === 'image'
                                            ? 'img_id'
                                            : 'test_case_id',
                                        val
                                      );
                                      updateOuputDocumentNodeMapping(
                                        op,
                                        'node_id',
                                        val?.id
                                      );
                                    }}
                                  />
                                ) : (
                                  <Autocomplete
                                    id="placeholder-autocomplete"
                                    name="placeholder"
                                    options={placeholderList}
                                    value={
                                      placeholderList.find(
                                        (option) =>
                                          option.id ===
                                          data[0]?.payload?.placeholder_id
                                      ) || null
                                    }
                                    popupIcon={<DropdownArrow />}
                                    disableClearable={['', undefined].includes(
                                      data[0]?.payload?.placeholder_id
                                    )}
                                    noOptionsText="No placeholder available."
                                    renderInput={renderPlaceholderInput}
                                    onChange={(e, val) => {
                                      updateOuputDocumentNodeMapping(
                                        op,
                                        'placeholder_id',
                                        val?.id
                                      );
                                    }}
                                    isOptionEqualToValue={
                                      placeholderEqualToValue
                                    }
                                    getOptionLabel={getOptionLabelPlaceholder}
                                  />
                                )}
                              </Grid>
                            </Grid>
                          );
                        })
                    : null}
                </Stack>
              )}
              {!formikForm.values.outputs.find((data) =>
                outputTypesCanbeAddedToDoc.includes(data.type)
              ) &&
                formikForm?.values?.attachDoc && (
                  <Box>
                    <Typography variant="body2">
                      Node doesn’t contain any output (chart, table, image) that
                      can be added in documentation.
                    </Typography>
                  </Box>
                )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {showToModelMetaDataOption ? (
          <>
            <Divider flexItem />
            <Accordion
              expanded={accordianState === 'panel5'}
              onChange={changeAccordionState('panel5')}
              disableGutters
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1bh-content"
                id="panel5bh-header"
              >
                <Typography sx={{ width: '33%', flexShrink: 0 }}>
                  Declared output to Vault mapping
                </Typography>
                {accordianState !== 'panel5' ? (
                  <Typography sx={{ color: typographyColor }} variant="body2">
                    Auto add output to Vault dashboard.
                  </Typography>
                ) : (
                  ''
                )}
              </AccordionSummary>
              <AccordionDetails>
                <Stack direction="column" spacing={3}>
                  <span
                    title={
                      pipelineData?.pipeline_setting?.enable_model_meta_data
                        ? 'Enable and map output to Vault dashboard.'
                        : 'Please enable adding output to Vault dashboard first under the pipeline settings.'
                    }
                  >
                    <Typography>Enable</Typography>
                    <Switch
                      name="enableAddingModelMetaData"
                      disabled={
                        !pipelineData?.pipeline_setting?.enable_model_meta_data
                      }
                      checked={
                        formikForm.values.enableAddingModelMetaData === true
                      }
                      onChange={(_e, checked) => {
                        formikForm.setFieldValue(
                          'enableAddingModelMetaData',
                          checked
                        );
                        if (!checked) {
                          formikForm.setFieldValue('modelMetaDataDetail', []);
                        }
                      }}
                    />
                  </span>
                  {formikForm.values.outputs.find((data) =>
                    outputTypesCanbeAddedToDoc.includes(data.type)
                  ) && (
                    <Stack
                      maxHeight={300}
                      sx={{
                        overflowY: 'auto',
                        overflowX: 'hidden',
                      }}
                      spacing={1}
                    >
                      {formikForm?.values?.enableAddingModelMetaData ? (
                        <Grid item container xs={12} spacing={1} ml={0.2}>
                          <Grid item xs={4}>
                            <Typography color="primary" variant="h6">
                              Ouput name
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography color="primary" variant="h6">
                              Output type
                            </Typography>
                          </Grid>

                          <Grid item xs={4}>
                            <Typography color="primary" variant="h6">
                              Mapped node from model metadata schema
                            </Typography>
                          </Grid>
                        </Grid>
                      ) : null}

                      {formikForm?.values?.enableAddingModelMetaData
                        ? formikForm?.values?.outputs
                            ?.filter((op) =>
                              outputTypesCanbeAddedToDoc.includes(op.type)
                            )
                            .map((op) => {
                              const data =
                                formikForm?.values?.modelMetaDataDetail?.filter(
                                  (op1) => op1?.payload?.actualId === op.key
                                );
                              return (
                                <Grid
                                  item
                                  container
                                  xs={12}
                                  key={op?.key}
                                  alignItems="center"
                                  spacing={1}
                                >
                                  <Grid
                                    item
                                    xs={4}
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    <Typography
                                      color="primary"
                                      variant="body1"
                                      title={op?.label}
                                    >
                                      {op?.label}
                                    </Typography>
                                  </Grid>
                                  <Grid
                                    item
                                    xs={4}
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      textTransform: 'capitalize',
                                    }}
                                  >
                                    <Typography
                                      color="primary"
                                      variant="body1"
                                      textTransform="capitalize"
                                      title={op?.type}
                                    >
                                      {op?.type}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={4}>
                                    <ModelMetaDataSchemaDropdown
                                      modelMetaDataSchema={modeleMetaDataSchema}
                                      placeholder="Select schema node"
                                      outputType={op?.type}
                                      defaultValue={
                                        data[0]?.payload?.section_name &&
                                        data[0]?.payload?.attribute_label
                                          ? `${data[0]?.payload?.section_name} / ${data[0]?.payload?.attribute_label}`
                                          : ''
                                      }
                                      // defaultValue={
                                      //   data[0]?.payload?.[
                                      //     op.type === 'table' ? 'table_id' : 'img_id'
                                      //   ]
                                      // }
                                      onNodeSelect={(sec, attr) => {
                                        updateOuputModelMetaDataSchemaNodeMapping(
                                          op,
                                          sec,
                                          attr
                                        );
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              );
                            })
                        : null}
                    </Stack>
                  )}
                  {!formikForm.values.outputs.find((data) =>
                    outputTypesCanbeAddedToDoc.includes(data.type)
                  ) &&
                    formikForm?.values?.attachDoc && (
                      <Box>
                        <Typography variant="body2">
                          Node doesn’t contain any output (chart, table, image)
                          that can be added in documentation.
                        </Typography>
                      </Box>
                    )}
                </Stack>
              </AccordionDetails>
            </Accordion>{' '}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button
          sx={{ marginLeft: '5px' }}
          variant="contained"
          onClick={submitOnClick}
          disabled={updateNodePermission !== 'Yes' || readOnly}
          title={updateTitle}
          aria-label={updateTitle}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(PipelineNodeEditorModal);

PipelineNodeEditorModal.defaultProps = {
  deleteNode: () => {},
  nodeData: {},
  permissions: 'View',
  docEditorOverview: {},
  modeleMetaDataSchema: null,
  showToModelMetaDataOption: false,
  readOnly: false,
};

PipelineNodeEditorModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  deleteNode: PropTypes.func,
  nodeData: PropTypes.oneOfType([PropTypes.object]),
  permissions: PropTypes.string,
  activeProject: PropTypes.oneOfType([PropTypes.object]).isRequired,
  datasetList: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object]))
    .isRequired,
  docEditorOverview: PropTypes.oneOfType([PropTypes.object]),
  pipelineData: PropTypes.oneOfType([PropTypes.object]).isRequired,
  fetchDatasetColumnDetails: PropTypes.func.isRequired,
  fetchAllModels: PropTypes.func.isRequired,
  updateNode: PropTypes.func.isRequired,
  fetchEditorOverview: PropTypes.func.isRequired,
  allPermissions: PropTypes.oneOfType([PropTypes.array]).isRequired,
  consoleFileList: PropTypes.oneOfType([PropTypes.array]).isRequired,
  modeleMetaDataSchema: PropTypes.oneOfType([PropTypes.object]),
  showToModelMetaDataOption: PropTypes.bool,
  fetchPods: PropTypes.func.isRequired,
  updateNodePermission: PropTypes.string.isRequired,
  deleteNodePermission: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  fetchConnectedPipelineArguemnts: PropTypes.func.isRequired,
};

const DisplayOutput = ({ output }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        marginTop: '2px',
        height: 'inherit',
        borderRadius: '6px',
      }}
    >
      <Accordion
        sx={{
          backgroundColor: theme.palette.other.white,
          boxShadow: 'none',
          '& .accordian-actions': {
            display: 'none',
          },
          '&:hover': {
            '& .accordian-actions': {
              display: 'flex',
            },
          },
        }}
        expanded={false}
      >
        <AccordionSummary
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{ position: 'relative' }}
        >
          <Grid xs={12} container>
            <Grid item xs={6}>
              <Typography variant="body2">Name: </Typography>
              <Typography variant="body1">
                {typeof output?.label === 'string' ? output?.label : ''}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">Type: </Typography>
              <Typography variant="body1">{output?.type}</Typography>
            </Grid>
          </Grid>
        </AccordionSummary>
      </Accordion>
    </Box>
  );
};

DisplayOutput.propTypes = {
  output: PropTypes.oneOfType([PropTypes.object]).isRequired,
};
